import React, { ReactNode, useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import CommissioningReportContext from '@totem/components/salesOrders/commissioning/CommissioningReportContext';
import {
  SalesOrderCommissioningReportItem,
  SalesOrderCommissioningReportItems,
} from '@totem/components/salesOrders/commissioning/types';
import SalesOrderLineEditModal from '@totem/components/salesOrders/edits/SalesOrderLineEditModal';
import TicketCreateModal from '@totem/components/ticketing/create/TicketCreateModal';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { SALES_ORDERS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  endpoint?: string;
  onRecordTotalChanged?: (total: number) => void;
  children?: ReactNode;
};

const CommissioningReportContainer = ({
  endpoint = `${SALES_ORDERS_ENDPOINT}/commissioning`,
  onRecordTotalChanged,
  children,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);

  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [reportData, setReportData] =
    useState<SalesOrderCommissioningReportItems>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [selectedRecord, setSelectedRecord] =
    useState<SalesOrderCommissioningReportItem>(null);
  const [defaultTab, setDefaultTab] = useState<string>('status');

  useEffect(() => {
    if (isNotNull(onRecordTotalChanged)) {
      onRecordTotalChanged(totalRecords);
    }
  }, [onRecordTotalChanged, totalRecords]);

  useEffect(() => {
    if (!refreshData) {
      return;
    }

    setRefreshData(false);

    setLoading(true);

    fetch(endpoint, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: SalesOrderCommissioningReportItems) => {
        const data = result ?? [];
        setReportData(data);

        setTotalRecords(data.length);
      })
      .then(() => setLoading(false));
  }, [refreshData, endpoint, getApiToken]);

  const handleAction = (
    actionName: string,
    record: SalesOrderCommissioningReportItem,
    tab: string = 'status',
  ) => {
    setAction(actionName);

    setSelectedRecord(record);

    setDefaultTab(tab);
  };

  const handleModalClose = (doRefresh?: boolean) => {
    setAction('');

    setSelectedRecord(null);
    if (doRefresh) {
      setRefreshData(true);
    }
  };

  const getTicketSummary = (): string => {
    if (!selectedRecord) {
      return 'Commissioning Report Issue';
    }
    const parts: string[] = [];
    if (selectedRecord.status.buildingIsMissing) {
      parts.push('Assign Building');
    }
    if (selectedRecord.status.deviceIsMissing) {
      parts.push('Install / Assign Device');
    }
    if (selectedRecord.status.deviceToolKeyMissing) {
      parts.push('Setup Device in Tools');
    }
    return parts.join(' / ') || 'Commissioning Report Issue';
  };

  const getTicketDescription = (): string => {
    if (!selectedRecord) {
      return '';
    }
    const sol = selectedRecord.salesOrder;
    const soRef = `Sales Order: ${sol.nsSalesOrder?.tranId ?? ''} / Line: ${sol.nsSalesOrderLine?.line ?? ''} - ${sol.nsSalesOrderLine?.description ?? ''}`;
    const services = (sol.services ?? []).map((svc) => svc.name).join(', ');
    return `Commissioning Report Issue:\n\n${soRef}\n\nServices:\n${services}\n\n${getTicketSummary()}`;
  };

  return (
    <CommissioningReportContext.Provider
      value={{
        loading,
        reportData,
        totalRecords,
        searchText,
        setSearchText,
        onAction: handleAction,
        refetch: () => setRefreshData(true),
      }}
    >
      {children}
      {action === 'EditLine' && selectedRecord !== null && (
        <SalesOrderLineEditModal
          salesOrderLine={selectedRecord.salesOrder}
          defaultTab={defaultTab}
          onClose={(updated) => {
            handleModalClose(updated);
          }}
        />
      )}
      {action === 'CreateTicket' && selectedRecord !== null && (
        <TicketCreateModal
          visible
          onClose={handleModalClose}
          keys={[
            {
              system: 'NETSUITE_SALES_ORDER',
              instance: 'NETSUITE_SALES_ORDER_KEY',
              key: selectedRecord.salesOrder.nsSalesOrder?.transactionId ?? '',
              uid: `NETSUITE_SALES_ORDER:NETSUITE_SALES_ORDER_KEY:${selectedRecord.salesOrder.nsSalesOrder?.transactionId ?? ''}`,
              createdByType: 'SYSTEM',
              createdByName: userProfile.email,
              createdById: userProfile.id,
              createdAt: dayjs().toISOString(),
            },
          ]}
          defaultSummary={getTicketSummary()}
          defaultDescription={getTicketDescription()}
          defaultCategory="IB Dashboard"
          defaultSubCategory="Provisioning"
        />
      )}
    </CommissioningReportContext.Provider>
  );
};

export default CommissioningReportContainer;
